import React, { useState } from 'react';
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Card
} from 'antd';
import passwordValidator from 'password-validator';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { LogoutUser } from '../../actions';
import { GlobalApiErrorHandler } from '../../shared/helpers';
import { InputBox, isBrowser, MsgBox } from '../../shared/custom-ant-d';
import { reqPostChangeCandidatePassword, reqPostLogout } from '../../axios/api';

const schema = new passwordValidator();

const layout = {
  labelCol: {
    md: {
      span: 8,
    },
    xs: {
      span: 24,
    }
  },
  wrapperCol: {
    md: {
      span: 16,
    },
    xs: {
      span: 24,
    }
  },
};

const tailLayout = {
  wrapperCol: {
    md: {
      offset: 8,
      span: 16,
    }
  },
};

const validateMessages = {
  required: '${label} is required!',
};

export const ChangePassword = () => {
  const Auth = useSelector(state => state.Auth);
  const history = useHistory();
  const dispatch = useDispatch();

  const [form] = Form.useForm();

  const [submitLoading, setSubmitLoading] = useState(false);

  const onFinish = (values) => {
    setSubmitLoading(true);
    // §11.3: send plaintext over TLS; Careers.Web encrypts both passwords server-side.
    submit(values.password, values.cur_password);
  };

  const submit = (newPassword, oldPassword) => {
    var formData = {
      CandidateID: Auth.candidateID,
      OldPassword: oldPassword,
      Password: newPassword
    };

    var formStr = JSON.stringify(formData);

    reqPostChangeCandidatePassword(formStr)
      .then(res => {
        setSubmitLoading(false);

        if (res.data.isSuccess) {
          sessionStorage.removeItem("candidate");

          dispatch(LogoutUser());

          MsgBox({
            type: 'success',
            message: 'Success!',
            description: "Password updated. Please re-login using your new password.",
          })

          reqPostLogout();
          return history.push('/login');
          //form.resetFields();

        } else {
          MsgBox({
            type: 'error',
            message: 'Error!',
            description: res.data.errorMessage,
          })

        }
      })
      .catch(err => {
        console.log(err);
        setSubmitLoading(false);
        GlobalApiErrorHandler({ errors: err.response.data.errors });
      });
  }

  return (
    <>
      <Row className="margin-to-nav">
        <Col sm={{ span: 18, offset: 2 }} xs={24}>
          <Card title="Change Password" className="iqor-header">
            <Form
              {...layout}
              className="label-left-align"
              name="change-password"
              onFinish={onFinish}
              validateMessages={validateMessages}
              form={form}
            >
              <Form.Item
                name="cur_password"
                label="Current Password"
                hasFeedback
                rules={[
                  {
                    required: true,
                  }
                ]}
              >
                {
                  (isBrowser) ?
                    (<Input.Password />) :
                    (<InputBox type={'password'} />)
                }
              </Form.Item>

              <Form.Item
                name="password"
                label="New Password"
                hasFeedback
                rules={[
                  {
                    required: true,
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('cur_password') !== value) {
                        return Promise.resolve();
                      }

                      return Promise.reject('New password cannot be the same as the old one.');
                    },
                  }),
                  () => ({
                    validator(_, value) {
                      schema
                        .is().min(8);
                      if (!schema.validate(value, { list: true }).includes('min')) {
                        return Promise.resolve();
                      } else {
                        return Promise.reject('Password needs to be at least 8 characters long.')
                      }
                    }
                  }),
                  () => ({
                    validator(_, value) {
                      schema
                        .is().max(20);
                      if (!schema.validate(value, { list: true }).includes('max')) {
                        return Promise.resolve();
                      } else {
                        return Promise.reject('Password must not be longer than 20 characters.')
                      }
                    }
                  }),
                  () => ({
                    validator(_, value) {
                      schema
                        .has().uppercase();

                      if (!schema.validate(value, { list: true }).includes('uppercase')) {
                        return Promise.resolve();
                      } else {
                        return Promise.reject('Password needs at least 1 uppercase character.')
                      }
                    }
                  }),
                  () => ({
                    validator(_, value) {
                      schema
                        .has().digits(2);

                      if (!schema.validate(value, { list: true }).includes('digits')) {
                        return Promise.resolve();
                      } else {
                        return Promise.reject('Password needs at least 2 numerical characters.')
                      }
                    }
                  }),
                  () => ({
                    validator(_, value) {
                      schema
                        .has().symbols();

                      if (!schema.validate(value, { list: true }).includes('symbols')) {
                        return Promise.resolve();
                      } else {
                        return Promise.reject('Password needs at least 1 special character.')
                      }
                    }
                  }), () => ({
                    validator(_, value) {
                      schema
                        .has().not().spaces();

                      if (!schema.validate(value, { list: true }).includes('spaces')) {
                        return Promise.resolve();
                      } else {
                        return Promise.reject('Password should not have spaces.')
                      }
                    }
                  }), () => ({
                    validator(_, value) {
                      schema
                        .has().lowercase();

                      if (!schema.validate(value, { list: true }).includes('lowercase')) {
                        return Promise.resolve();
                      } else {
                        return Promise.reject('Password needs at least 1 lowercase character.')
                      }
                    }
                  }),
                ]}
              >
                {
                  (isBrowser) ?
                    (<Input.Password />) :
                    (<InputBox type={'password'} />)
                }
              </Form.Item>

              <Form.Item
                name="confirm"
                label="Confirm Password"
                dependencies={['password']}
                hasFeedback
                rules={[
                  {
                    required: true,
                    message: 'Please confirm your password!',
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('password') === value) {
                        return Promise.resolve();
                      }

                      return Promise.reject('The two passwords that you entered do not match!');
                    },
                  }),
                ]}
              >
                {
                  (isBrowser) ?
                    (<Input.Password />) :
                    (<InputBox type={'password'} />)
                }
              </Form.Item>

              <Form.Item {...tailLayout}>
                <Button type="danger" htmlType="submit" loading={submitLoading} style={{ float: "right" }}>
                  Submit
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>
      </Row>
    </>
  );
}
